import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  DatePicker,
  Radio,
  Typography,
  Button,
  Space,
} from "antd";
import styled from "styled-components";
import container from "container/BrokerDashboard/editAgentContainer";
import moment from "moment";
import { LoadingOutlined } from "@ant-design/icons";

const { Title } = Typography;

// Function to extract agent ID from URL
const getAgentIdFromUrl = () => {
  const pathname = window.location.pathname;
  const match = pathname.match(/\/broker\/dashboard\/(\d+)\/orientation-info/);
  return match ? parseInt(match[1]) : null;
};

const SuccessMessage = styled.div`
  border-radius: 8px;
  background: var(--Success-600, #16a34a);
  position: fixed;
  right: 79.5px;
  top: 84px;
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: white;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 5px;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 0px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const FormSection = styled.div`
  margin-bottom: 15px;
`;

const SectionTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 0px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledFormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    color: #262626;
    font-weight: 500;
    font-size: 14px;
  }

  .ant-input,
  .ant-picker {
    border: 1px solid #d9d9d9;
    border-radius: 8px !important;
    background: #ffffff;
    color: #262626;
    height: 40px;

    &:hover {
      border-color: #bfbfbf;
    }

    &:focus {
      border-color: #722ed1;
      box-shadow: 0 0 0 2px rgba(114, 46, 209, 0.1);
    }
  }

  .ant-input[disabled],
  .ant-picker-disabled {
    background: #ffffff;
    color: #262626;
    border-color: #d9d9d9;
  }

  .ant-picker {
    height: 40px !important;
    border-radius: 8px !important;

    .ant-picker-input > input {
      height: 38px;
      line-height: 38px;
      border-radius: 8px;
    }
  }

  .ant-picker-disabled {
    background: #ffffff !important;
    color: #262626 !important;
    border-color: #d9d9d9 !important;

    .ant-picker-input > input {
      background: #ffffff !important;
      color: #262626 !important;
    }

    .ant-picker-suffix {
      color: #bfbfbf !important;
    }
  }

  .ant-radio-group {
    .ant-radio-wrapper {
      color: #262626;
      font-size: 14px;

      .ant-radio {
        .ant-radio-inner {
          border-color: #d9d9d9;

          &:hover {
            border-color: #722ed1;
          }
        }

        &.ant-radio-checked .ant-radio-inner {
          border-color: #722ed1;

          &::after {
            background-color: #722ed1;
          }
        }
      }
    }
  }

`;

const BorderedBox = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 20px;
  padding-bottom: 5px;
  margin-bottom: 10px;
`;

const PersonalInfoSection = ({
  getOnboardingPersonalInfoResponse,
  updateOnboardingInfo,
  getOnboardingPersonalInfo,
}) => {
  const { data: personalInfoResponse } =
    getOnboardingPersonalInfoResponse || {};
  const [isEditable, setIsEditable] = useState(false);
  const [form] = Form.useForm();
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false); // <-- Add loading state

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    form.setFieldsValue({
      firstName: personalInfoResponse?.firstname || "",
      lastName: personalInfoResponse?.lastname || "",
      phone: personalInfoResponse?.phone || "",
      birthday: personalInfoResponse?.birthday
        ? moment(personalInfoResponse.birthday)
        : null,
    });
  }, [personalInfoResponse, form]);

  const handleCancel = () => {
    form.setFieldsValue({
      firstName: personalInfoResponse?.firstname || "",
      lastName: personalInfoResponse?.lastname || "",
      phone: personalInfoResponse?.phone || "",
      birthday: personalInfoResponse?.birthday
        ? moment(personalInfoResponse.birthday)
        : null,
    });
    setIsEditable(false);
  };

  const handleToggle = async () => {
    if (isEditable) {
      try {
        setIsSaving(true); // Disable save button
        const values = await form.validateFields();

        const mainPayload = {
          data: {
            firstname: values.firstName,
            lastname: values.lastName,
            phone: values.phone,
            birthday: values.birthday
              ? values.birthday.format("YYYY-MM-DD")
              : null,
          },
          agent_id: getAgentIdFromUrl(),
          step_completed: "personal_info",
        };

        updateOnboardingInfo({
          payload: mainPayload,
          callback: () => {
          
            getOnboardingPersonalInfo({
              agentId: getAgentIdFromUrl(),
              step: "personal_info",
            });
            window.location.reload();
            setIsEditable(false);
            setSuccessMessage("Personal information updated successfully!");
            setIsSaving(false); // Re-enable save button
          },
        });
      } catch (errorInfo) {
        setIsSaving(false); // Re-enable save button on error
        console.warn("Validation Failed:", errorInfo);
      }
    } else {
      setIsEditable(true);
    }
  };

  return (
    <div>
      {successMessage && (
        <SuccessMessage>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.3333 4L6 11.3333L2.66667 8"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Personal information has been updated
        </SuccessMessage>
      )}
      <SectionHeader>
        <HeaderContent>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.75 9.75C6.75 9.33579 7.08579 9 7.5 9H16.5C16.9142 9 17.25 9.33579 17.25 9.75C17.25 10.1642 16.9142 10.5 16.5 10.5H7.5C7.08579 10.5 6.75 10.1642 6.75 9.75ZM7.5 13.5H16.5C16.9142 13.5 17.25 13.1642 17.25 12.75C17.25 12.3358 16.9142 12 16.5 12H7.5C7.08579 12 6.75 12.3358 6.75 12.75C6.75 13.1642 7.08579 13.5 7.5 13.5ZM21.75 5.25V19.5C21.7499 19.7599 21.6152 20.0012 21.394 20.1378C21.1729 20.2743 20.8968 20.2866 20.6644 20.1703L18 18.8381L15.3356 20.1703C15.1244 20.276 14.8756 20.276 14.6644 20.1703L12 18.8381L9.33562 20.1703C9.12436 20.276 8.87564 20.276 8.66438 20.1703L6 18.8381L3.33562 20.1703C3.1032 20.2866 2.82712 20.2743 2.60597 20.1378C2.38483 20.0012 2.25014 19.7599 2.25 19.5V5.25C2.25 4.42157 2.92157 3.75 3.75 3.75H20.25C21.0784 3.75 21.75 4.42157 21.75 5.25ZM20.25 5.25H3.75V18.2869L5.66437 17.3288C5.87564 17.223 6.12436 17.223 6.33563 17.3288L9 18.6619L11.6644 17.3288C11.8756 17.223 12.1244 17.223 12.3356 17.3288L15 18.6619L17.6644 17.3288C17.8756 17.223 18.1244 17.223 18.3356 17.3288L20.25 18.2869V5.25Z"
              fill="#111827"
            />
          </svg>
          <SectionTitle>Personal Information</SectionTitle>
        </HeaderContent>
        {!isEditable ? (
          <Button
            type="default"
            style={{
              border: "1px solid #6366F1",
              borderRadius: "8px",
              background: "#6366F1",
              color: "white",
              fontWeight: 500,
              height: "36px",
              padding: "0 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onClick={handleToggle}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.41421 3.25253C6.19526 4.03358 6.19526 5.29991 5.41421 6.08096C4.63316 6.86201 3.36683 6.86201 2.58579 6.08096C1.80474 5.29991 1.80474 4.03358 2.58579 3.25253C3.36683 2.47149 4.63316 2.47149 5.41421 3.25253"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.3333 4.66659H6"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.4142 9.91904C14.1953 10.7001 14.1953 11.9664 13.4142 12.7475C12.6332 13.5285 11.3668 13.5285 10.5858 12.7475C9.80474 11.9664 9.80474 10.7001 10.5858 9.91904C11.3668 9.13799 12.6332 9.13799 13.4142 9.91904"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.6665 11.3333H9.99984"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Edit
          </Button>
        ) : (
          <Space>
            <Button type="text" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="default"
              style={{
                borderRadius: "8px",
                border: "1px solid #6366F1",
                background: "#6366F1",
                boxShadow:
                  "0 1px 2px 0 rgba(16, 24, 40, 0.04), 0 1px 2px 0 rgba(16, 24, 40, 0.04)",
                color: "white",
                fontWeight: 500,
                height: "36px",
                padding: "0 16px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                minWidth: 90, // optional: keeps button width stable
              }}
              onClick={handleToggle}
              disabled={isSaving}
            >
              {isSaving ? (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <LoadingOutlined style={{ fontSize: 16, color: "white" }} spin />
                  Saving...
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M10.6663 12.6667H11.9997C13.4724 12.6667 14.6663 11.4728 14.6663 10V10C14.6663 8.52725 13.4724 7.33334 11.9997 7.33334V7.33334C11.9997 5.1242 10.2088 3.33334 7.99967 3.33334V3.33334C6.2878 3.33418 4.76634 4.42462 4.21554 6.04546C2.56745 6.26593 1.33598 7.67057 1.33301 9.33334H1.33301C1.33301 11.1743 2.82539 12.6667 4.66634 12.6667H5.33301"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M9.33366 10.6667L8.00033 9.33334L6.66699 10.6667"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8.00033 12.6667V9.33334"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Save
                </div>
              )}
            </Button>
          </Space>
        )}
      </SectionHeader>
      <Form layout="vertical" form={form}>
        <TwoColumnGrid>
          <StyledFormItem label="First name" name="firstName">
            <Input placeholder="Enter first name" disabled={!isEditable} />
          </StyledFormItem>
          <StyledFormItem label="Last name" name="lastName">
            <Input placeholder="Enter last name" disabled={!isEditable} />
          </StyledFormItem>
        </TwoColumnGrid>
        <TwoColumnGrid>
          <StyledFormItem label="Phone number" name="phone">
            <Input 
              placeholder="Enter phone number" 
              disabled={!isEditable}
              maxLength={12}
              value={form.getFieldValue('phone')}
              onChange={(e) => {
                let value = e.target.value;
                
                // Allow backspace/delete
                if (value.length < (form.getFieldValue('phone') || '').length) {
                  form.setFieldsValue({ phone: value });
                  return;
                }

                // Only keep numbers
                value = value.replace(/\D/g, '');

                // Format as user types
                if (value.length <= 3) {
                  form.setFieldsValue({ phone: value });
                } else if (value.length <= 6) {
                  form.setFieldsValue({ 
                    phone: `${value.slice(0,3)}-${value.slice(3)}`
                  });
                } else if (value.length <= 10) {
                  form.setFieldsValue({
                    phone: `${value.slice(0,3)}-${value.slice(3,6)}-${value.slice(6)}`
                  });
                }
              }}
            />
          </StyledFormItem>
          <StyledFormItem label="Birthday" name="birthday">
            <DatePicker style={{ width: "100%" }} disabled={!isEditable} />
          </StyledFormItem>
        </TwoColumnGrid>
      </Form>
    </div>
  );
};

export default container(PersonalInfoSection);
